<template>
  <div
    class="product"
    :class="classes"
  >
    <div
      v-if="Object.keys($props.marker).length > 0"
      class="product__marker"
    >
      <div
        v-tooltip="$props.marker.tooltip"
        class="marker"
      >
        <span
          class="body-3"
          v-text="$props.marker.text"
        />
        <the-icon
          art="light"
          name="info-circle"
        />
      </div>
    </div>

    <div
      v-if="$props.level"
      class="product__level"
    >
      <the-icon
        art="thin"
        :name="benefitLevel"
      />
    </div>

    <div
      class="product__name body-2"
      v-bind="$attrs"
      v-html="$t(`products.${$props.productId}.name`)"
    />

    <div
      class="product__designation h3"
      :data-product-id="$props.productId"
      v-html="$t(`products.${$props.productId}.designation`)"
    />

    <div class="product__pricing">
      <basic-product-pricing
        v-if="!$props.hiddenItems.includes('pricing')"
        :product="$props.product"
      />
    </div>

    <div class="product__description body-2">
      <span v-html="$t(`products.${$props.productId}.description`)" />

      <ul v-if="!$props.hiddenItems.includes('altDescription') && $te(`products.${$props.productId}.altDescription`)">
        <template
          v-for="item in $t(`products.${$props.productId}.altDescription`)"
          :key="item"
        >
          <li v-html="item" />
        </template>
      </ul>
    </div>

    <div class="product__messages">
      <template v-if="!$props.hiddenItems.includes('messages')">
        <!-- put all messages and stuff in here! -->
        <template
          v-for="message in props.product.messages"
          :key="message.key"
        >
          <basic-message
            :closeable="false"
            :severity="message.type.toLowerCase()"
            :content="$t(`error.${message.key}`)"
          />
        </template>

        <basic-message
          v-if="$props.productState !== PRODUCT_STATE.EDITABLE"
          class="product__message"
          severity="warning"
          :closeable="false"
          :content="$t('product.detail.cancellation.message')"
        />

        <basic-message
          v-if="hasCalculationWarnings"
          class="product__message"
          :closeable="false"
          :content="detailMessage"
        />
      </template>
    </div>

    <div class="product__options">
      <product-options
        v-if="!$props.hiddenItems.includes('options')"
        :mode="$props.options"
        :category-id="$props.categoryId"
        :group-id="$props.groupId"
        :person="$props.person"
        :product="$props.product"
        :show-doctor-reduction="false"
        :source="$props.source"
        @change="event => emits('change', event)"
      />
    </div>

    <div class="product__price">
      <div v-if="!$props.hiddenItems.includes('price')">
        <template v-if="$props.isLoading">
          <img :src="LoadingIcon" />
        </template>

        <basic-product-price
          v-else
          class="lead-1"
          :product="$props.product"
          :prefix="prefix"
          :show-zero-value="options.reduction < 0"
          :suffix="$t('product.currency')"
        >
          <span
            v-if="['ANETProdukt', 'OHAMProdukt'].includes($props.product.productId) && !$props.product.doctor"
            class="price__asterisk body-2"
            v-text="'*'"
          />
        </basic-product-price>
      </div>
    </div>

    <div
      v-if="!$props.hiddenItems.includes('actions') || $slots.actions"
      class="product__actions"
      :class="{
        'appearance-extended': $props.product.selected,
        'product__actions--loading': $props.isLoading,
      }"
    >
      <slot name="actions" />

      <product-actions
        v-if="!$props.hiddenItems.includes('actions')"
        :product="$props.product"
        :product-state="$props.productState"
        :person-id="$props.personId"
        :is-doctor-search-skipped="$props.isDoctorSearchSkipped"
        @add="event => addProduct(event)"
        @open-detail="productDetailModalRef.modalRef.open()"
        @remove="event => $emit('remove', event)"
        @search="event => $emit('search', event)"
      />
    </div>

    <slot
      :set-ref="setRef"
      name="modal"
    >
      <dvp-product-modal
        v-if="!$props.hiddenItems.includes('modal')"
        :id="`productDetailModal`"
        ref="productDetailModalRef"
        :category-id="$props.categoryId"
        :group-id="$props.groupId"
        :modal-title="forcedOpen ? $t('product.criteriaInformation') : null"
        :options="$props.modalOptions"
        :person="person"
        :product="$props.product"
        :source="$props.source"
        @close="forcedOpen = false"
      />
    </slot>
  </div>
</template>

<script setup>
  import { computed, ref } from 'vue'

  import useI18n from '@shared/hooks/useI18n'
  import usePersonSpecificProduct from '@shared/hooks/usePersonSpecificProduct'
  import useProduct from '@shared/hooks/useProduct'

  import ProductActions from '@shared/components/Product/ProductActions'
  import DvpProductModal from '@dvp/components/ProductModal'
  import ProductOptions from '@shared/components/Product/Options'

  import { BasicMessage, BasicProductPrice, BasicProductPricing } from '@shared/components/Basic'

  import LoadingIcon from '@shared/assets/loading.svg'
  import { CATEGORY_TYPE, PRODUCT_STATE } from '@shared/config/constants'
  import { SOURCE } from '@shared/config/events'
  import { logWarning } from '@shared/helpers/Logger'

  // HOOKS
  const { t } = useI18n()
  const { getProductMessages } = usePersonSpecificProduct()
  const { hasOptions } = useProduct()

  // INIT
  const emits = defineEmits(['add', 'change', 'remove', 'search'])
  const props = defineProps({
    categoryId: {
      type: String,
      required: true,
    },

    groupId: {
      type: String,
      required: true,
    },

    isLoading: {
      type: Boolean,
      default: false,
    },

    level: {
      type: Number,
      default: 0,
    },

    hiddenItems: {
      type: Array,
      default: () => [],
    },

    marker: {
      type: Object,
      default: () => ({}),
      validator(val) {
        if (!val || Object.keys(val).length === 0) return true
        const isValid = Object.hasOwn(val, 'text') && Object.hasOwn(val, 'tooltip')
        if (!isValid) {
          logWarning('[Vue warn]: Invalid prop: marker should have the following properties: {text, tooltip}')
        }
        return isValid
      }
    },

    options: {
      type: String,
      default: 'read',
    },

    person: {
      type: Object,
      required: true,
    },

    personId: {
      type: String,
      required: true,
    },

    pricePrefix: {
      type: String,
      default: null,
    },

    product: {
      type: Object,
      required: true,
    },

    productId: {
      type: String,
      required: true,
    },

    productState: {
      type: String,
      default: null,
    },

    isDoctorSearchSkipped: {
      type: Boolean,
      default: false
    },

    source: {
      type: String,
      default: SOURCE.CONFIGURATOR,
    },
  })

  // DATA
  const forcedOpen = ref()
  const productDetailModalRef = ref()

  // COMPUTED
  // eslint-disable-next-line vue/return-in-computed-property
  const benefitLevel = computed(() => {
    switch (props.level) {
      case 1:
        return 'gauge-low'

      case 2:
        return 'gauge'

      case 3:
        return 'gauge-max'
    }
  })

  const classes = computed(() => {
    return {
      'appearance-extended': props.product.selected,
      'product--selected': props.product.selected,
      'product--invalid': !props.product.valid && !!props.product.messages.find(m => m.type === 'ERROR'),
      'product--unavailable': !props.product.valid && !!props.product.messages.find(m => m.type === 'INFO'),
    }
  })

  const detailMessage = computed(() => {
    return t('product.detail.message', { servicePortalText: props.product.calculationWarnings[0] })
  })

  const hasCalculationWarnings = computed(() => {
    return props.product.calculationWarnings?.length > 0
  })

  const prefix = computed(() => {
    if (props.pricePrefix) return props.pricePrefix

    return props.product?.showDiscountGroup && !props.product?.doctor ? t('product.asof') : ''
  })

  // METHODS
  function addProduct(event) {
    const hasMessages =
      getProductMessages({
        categoryId: props.categoryId,
        hasCalculationWarnings: hasCalculationWarnings.value,
        modalRef: productDetailModalRef.value,
        personId: props.person.personId,
        product: props.product,
        productState: props.productState,
      }).length > 0

    const __hasOptions = props.categoryId !== CATEGORY_TYPE.KVG && hasOptions(props.product)
    if (!event.forceAdd && props.options !== 'write' && (hasMessages || __hasOptions)) {
      forcedOpen.value = true
      productDetailModalRef.value.modalRef.open()
    } else {
      emits('add', event)
    }
  }

  function setRef(el) {
    productDetailModalRef.value = el
  }
</script>

<style scoped>
  .product {
    position: relative;
    cursor: default;
    display: grid;
    grid-row: 1 / 9;
    grid-template-columns: 1fr;
    grid-template-rows: subgrid;

    border: 1px solid var(--outline-variant);
    border-radius: var(--fixed-border-radius-fix-03);
    padding: var(--fixed-spacing-fix-06) var(--dotcom-responsive-spacing-res-s) var(--fixed-spacing-fix-07)
      var(--dotcom-responsive-spacing-res-s);
    margin: 0 0 var(--spacing-san-spacing-04) 0;

    &--selected {
      background-color: var(--surface-ext-b-low);
      box-shadow: var(--elevation-level-2);
      border-color: var(--surface-ext-b-low);
    }

    &--invalid {
      color: var(--Interaction-States-disabled-default-label);
    }

    &--unavailable {
      color: var(--on-surface-low);
    }

    .product__marker {
      position: absolute;
      top: -13px;

      text-align: center;
      height: 28px;
      width: 100%;
      display: flex;
      justify-content: center;

      .marker {
        cursor: pointer;
        position: relative;
        display: flex;
        align-items: center;
        gap: 4px;
        padding: var(--fixed-spacing-fix-01) var(--fixed-spacing-fix-02);
        background-color: var(--surface-ext-b-medium);
        border-radius: 0 0 var(--radius-border-radius-02, 8px) var(--radius-border-radius-02, 8px);
        box-shadow: var(--elevation-level-2);

        .icon {
          font-size: 14px;
        }

        &:after {
          position: absolute;
          content: '';

          width: 0;
          height: 0;
          right: -11px;
          top: 4.5px;
          border-left: 8px solid transparent;
          border-right: 8px solid transparent;

          border-top: 8px solid var(--surface-ext-b-high);
          transform: rotate(45deg);
        }
      }
    }

    .product__level {
      position: absolute;
      right: 14px;
      top: 32px;
      font-size: 24px;
    }

    .product__designation,
    .product__description {
      margin: var(--fixed-spacing-fix-04) 0 0;
    }

    .product__options:not(:empty),
    .product__price,
    .product__actions {
      margin: var(--dotcom-responsive-spacing-res-xxs) 0 0;
    }

    .product__messages .product__message,
    .product__pricing:not(:empty) {
      margin: var(--fixed-spacing-fix-02) 0 0;
    }

    .product__description ul {
      padding: 0 0 0 var(--fixed-spacing-fix-06);
      margin: var(--fixed-spacing-fix-01) 0 0 0;
    }

    .product__price {
      position: relative;

      :deep(.price:has(.price__asterisk)) {
        .price__suffix {
          padding-left: var(--fixed-spacing-fix-04);
        }
      }

      .price__asterisk {
        position: absolute;
        top: 2px;
      }
    }

    .product__actions {
      align-self: end;
    }
  }

  img {
    text-align: center;
    position: absolute;
    top: -20px;
    height: 40px;
    width: 40px;
    left: calc(50% - 20px);
  }

  @media (--v-medium) {
  }

  @media (--v-large) {
  }

  @media (--v-wide) {
  }

  @media (--v-ultra) {
  }

  @media (--v-max) {
  }
</style>
